

import { createContext, useContext, useMemo } from "react";
import { Event } from "@/types/Event";
import { EventRoleDTO } from "@/types/EventRoleDTO";

type EventContextType = {
  event: Event;
  /**
   * Optional because the builder routes can supply the event but not always a
   * resolved role — they sit outside EventLayoutShell and pass RoleProvider its
   * props directly. Both readers already guard with `eventRole?.`, so this only
   * makes the type say what the runtime already did.
   */
  eventRole?: EventRoleDTO;
  /**
   * True while `event` is NOT the route's event but the previous one, kept
   * painted as `keepPreviousData` placeholder while the next event loads
   * (`useEventLayoutEventQuery`). This is the ONLY staleness signal there is:
   * the shell's event projection carries no `eventId`, so comparing
   * `event.eventId` with the route can never detect it. A reader whose result
   * is WRITTEN somewhere (an import parsed in the event zone) must not use the
   * placeholder's values; chrome that merely paints may. Absent/false outside
   * EventLayoutShell — the builder routes pass a settled event.
   */
  eventIsPlaceholder?: boolean;
};

export const EventContext = createContext<EventContextType | undefined>(undefined);

export function EventProvider({
  children,
  event,
  eventRole,
  eventIsPlaceholder = false,
}: {
  children: React.ReactNode;
  event: Event;
  eventRole?: EventRoleDTO;
  /** `isPlaceholderData` of the query that produced `event` — see the type. */
  eventIsPlaceholder?: boolean;
}) {
  const value = useMemo(
    () => ({ event, eventRole, eventIsPlaceholder }),
    [event, eventRole, eventIsPlaceholder],
  );

  return (
    <EventContext.Provider value={value}>
      {children}
    </EventContext.Provider>
  );
}

export function useEventContext() {
  const ctx = useContext(EventContext);
  if (!ctx)
    throw new Error("useEventContext must be used within EventProvider");
  return ctx;
}

/**
 * The event, or nothing — for surfaces that are reused OUTSIDE an event scope.
 *
 * <p>Twin of `useCapabilitiesOptional`, and it exists for the same reason: a
 * table cell is rendered by the guest list (inside an EventProvider) and by
 * tables that are not event-scoped at all, so a cell that needs to know whether
 * the SEATING plugin is on cannot use the throwing reader. Absent context means
 * "no event", which every caller must read as "do not offer the action".
 */
export function useEventContextOptional() {
  return useContext(EventContext);
}

/**
 * Resolve the active timezone for date pickers and DATE_TIME displays. Returns
 * the event's IANA tz when called inside an event-scoped surface; falls back
 * to the browser's tz on dashboard/global surfaces.
 */
export function useEventTimezone(): string {
  const ctx = useContext(EventContext);
  return (
    ctx?.event?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
  );
}
